import { FC, useState } from 'react';
import styles from './TimesSection.module.scss';
import ExpandableSelector from 'components/UI/V3/ExpandableSelector';
import { ExpandableSelectorList, ExpandableSelectorItem } from 'components/UI/V3/ExpandableSelector/List';
import { getBusinessTimeOptions } from 'utils/TimeOptionsUtils';

interface TimesSectionProps {
    className?: string;
    dropOffTime: { from: string; to: string };
    pickUpTime: { from: string; to: string };
    onDropOffTimeChange: (type: 'from' | 'to', time: string) => void;
    onPickUpTimeChange: (type: 'from' | 'to', time: string) => void;
}

const isTimeGreaterThan = (time1: string, time2: string): boolean => {
    const timeRegex = /(\d+):(\d+)\s*(AM|PM)/i;
    const match1 = time1.match(timeRegex);
    const match2 = time2.match(timeRegex);

    if (!match1 || !match2) {
        return false;
    }

    const [_, hour1, minute1, period1] = match1;
    const [__, hour2, minute2, period2] = match2;

    let hour1Num = parseInt(hour1);
    let hour2Num = parseInt(hour2);

    // Convert to 24h format
    if (period1.toUpperCase() === 'PM' && hour1Num !== 12) hour1Num += 12;
    if (period1.toUpperCase() === 'AM' && hour1Num === 12) hour1Num = 0;
    if (period2.toUpperCase() === 'PM' && hour2Num !== 12) hour2Num += 12;
    if (period2.toUpperCase() === 'AM' && hour2Num === 12) hour2Num = 0;

    // Compare hours
    if (hour1Num !== hour2Num) {
        return hour1Num > hour2Num;
    }

    // If hours are equal, compare minutes
    return parseInt(minute1) > parseInt(minute2);
};

export const TimesSection: FC<TimesSectionProps> = ({
    dropOffTime,
    pickUpTime,
    onDropOffTimeChange,
    onPickUpTimeChange,
    className
}) => {
    const [dropOffFromExpanded, setDropOffFromExpanded] = useState(false);
    const [dropOffToExpanded, setDropOffToExpanded] = useState(false);
    const [pickUpFromExpanded, setPickUpFromExpanded] = useState(false);
    const [pickUpToExpanded, setPickUpToExpanded] = useState(false);

    const renderDropOffTimeOptions = (type: 'from' | 'to') => {
        const timeOptions = getBusinessTimeOptions(6, 22, 15, '12h').map(option => option.label);

        return timeOptions.map(time => {
            // Check if the time is greater than the drop off time
            const isDisabled =
                type === 'from' ? isTimeGreaterThan(time, dropOffTime.to) : isTimeGreaterThan(dropOffTime.from, time);

            return (
                <ExpandableSelectorItem
                    key={time}
                    onClick={() => !isDisabled && onDropOffTimeChange(type, time)}
                    selected={type === 'from' ? dropOffTime.from === time : dropOffTime.to === time}
                    disabled={isDisabled}
                >
                    {time}
                </ExpandableSelectorItem>
            );
        });
    };

    const renderPickUpTimeOptions = (type: 'from' | 'to') => {
        const timeOptions = getBusinessTimeOptions(6, 22, 15, '12h').map(option => option.label);
        return timeOptions.map(time => {
            // Check if the time is greater than the pick up time
            const isDisabled =
                type === 'from' ? isTimeGreaterThan(time, pickUpTime.to) : isTimeGreaterThan(pickUpTime.from, time);

            return (
                <ExpandableSelectorItem
                    key={time}
                    onClick={() => !isDisabled && onPickUpTimeChange(type, time)}
                    selected={type === 'from' ? pickUpTime.from === time : pickUpTime.to === time}
                    disabled={isDisabled}
                >
                    {time}
                </ExpandableSelectorItem>
            );
        });
    };

    return (
        <div className={`${styles.timeContainer} ${className || ''}`}>
            {/* Drop Off Time */}
            <div className={styles.timeSection}>
                <label className={styles.timeLabel}>
                    Drop Off Time <span className={styles.required}>*</span>
                </label>
                <div className={styles.timeRow}>
                    <div className={styles.timeField}>
                        <span className={styles.timeSubLabel}>From</span>
                        <ExpandableSelector
                            id="dropoff-from"
                            label={dropOffTime.from}
                            expanded={dropOffFromExpanded}
                            dropdownAlign="right"
                            onExpand={() => setDropOffFromExpanded(!dropOffFromExpanded)}
                            onClickAway={() => setDropOffFromExpanded(false)}
                            className={styles.timeSelector}
                        >
                            <ExpandableSelectorList>{renderDropOffTimeOptions('from')}</ExpandableSelectorList>
                        </ExpandableSelector>
                    </div>
                    <div className={styles.timeField}>
                        <span className={styles.timeSubLabel}>To</span>
                        <ExpandableSelector
                            id="dropoff-to"
                            label={dropOffTime.to}
                            expanded={dropOffToExpanded}
                            dropdownAlign="right"
                            onExpand={() => setDropOffToExpanded(!dropOffToExpanded)}
                            onClickAway={() => setDropOffToExpanded(false)}
                            className={styles.timeSelector}
                        >
                            <ExpandableSelectorList>{renderDropOffTimeOptions('to')}</ExpandableSelectorList>
                        </ExpandableSelector>
                    </div>
                </div>
            </div>

            {/* Pick Up Time */}
            <div className={styles.timeSection}>
                <label className={styles.timeLabel}>
                    Pick Up Time <span className={styles.required}>*</span>
                </label>
                <div className={styles.timeRow}>
                    <div className={styles.timeField}>
                        <span className={styles.timeSubLabel}>From</span>
                        <ExpandableSelector
                            id="pickup-from"
                            label={pickUpTime.from}
                            expanded={pickUpFromExpanded}
                            dropdownAlign="right"
                            onExpand={() => setPickUpFromExpanded(!pickUpFromExpanded)}
                            onClickAway={() => setPickUpFromExpanded(false)}
                            className={styles.timeSelector}
                        >
                            <ExpandableSelectorList>{renderPickUpTimeOptions('from')}</ExpandableSelectorList>
                        </ExpandableSelector>
                    </div>
                    <div className={styles.timeField}>
                        <span className={styles.timeSubLabel}>To</span>
                        <ExpandableSelector
                            id="pickup-to"
                            label={pickUpTime.to}
                            expanded={pickUpToExpanded}
                            dropdownAlign="right"
                            onExpand={() => setPickUpToExpanded(!pickUpToExpanded)}
                            onClickAway={() => setPickUpToExpanded(false)}
                            className={styles.timeSelector}
                        >
                            <ExpandableSelectorList>{renderPickUpTimeOptions('to')}</ExpandableSelectorList>
                        </ExpandableSelector>
                    </div>
                </div>
            </div>
        </div>
    );
};
